import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, Lock, Plus } from "lucide-react";
import { isDiaryAvailable } from "../lib/availability";
import { finalizePastDays, loadAllPosts, loadDailyMoodMap, moodValueToColor, toDateKey } from "../lib/mood";

const DIARY_LEGACY_KEY = "rl_diary_v1";
const DIARY_POSTS_KEY = "rl_diary_posts_v1";

function monthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, delta) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function toRgba(rgb, alpha = 0.14) {
  const match = String(rgb || "").match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/i);
  if (!match) return `rgba(15, 23, 42, ${alpha})`;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
}

function defaultSiteTint() {
  return "rgba(20, 184, 166, 0.10)";
}

function postTitle(post) {
  const clean = String(post?.title || "").trim();
  return clean || "Journal entry";
}

function previewText(post) {
  const raw = String(post?.content || post?.title || "").replace(/\s+/g, " ").trim();
  if (!raw) return "Journal entry";
  return raw.length > 44 ? `${raw.slice(0, 44)}…` : raw;
}

function postToneClass(post) {
  const type = String(post?.type || "journaling").toLowerCase();
  if (type === "relapse") return "bg-red-500/70 text-white";
  if (type === "trigger") return "bg-amber-400/75 text-slate-900";
  return "bg-emerald-500/70 text-slate-900";
}

function buildCells(anchorMonth) {
  const first = monthStart(anchorMonth);
  const startOffset = first.getDay();
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const weekRows = Math.ceil((startOffset + daysInMonth) / 7);
  const totalCells = weekRows * 7;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startOffset);
  return Array.from({ length: totalCells }, (_, idx) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + idx);
    return d;
  });
}

function migrateLegacyIfNeeded() {
  try {
    const raw = localStorage.getItem(DIARY_LEGACY_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    const posts = loadAllPosts();
    const now = Date.now();
    Object.entries(data).forEach(([dateKey, text]) => {
      if (!text) return;
      const arr = posts[dateKey] || [];
      if (arr.length > 0) return;
      arr.push({ id: `${dateKey}-legacy`, ts: now, title: "Journal", type: "journaling", content: text, imageUrl: "" });
      posts[dateKey] = arr;
    });
    localStorage.setItem(DIARY_POSTS_KEY, JSON.stringify(posts));
    localStorage.removeItem(DIARY_LEGACY_KEY);
  } catch (_) {}
}

export default function DiaryCalendar() {
  const nav = useNavigate();
  const available = React.useMemo(() => isDiaryAvailable(), []);
  const [search] = useSearchParams();
  const [currentMonth, setCurrentMonth] = React.useState(() => {
    const q = (search.get("date") || "").trim();
    if (q) {
      const [y, m] = q.split("-").map(Number);
      if (y && m) return new Date(y, m - 1, 1);
    }
    return monthStart(new Date());
  });
  const [postsMap, setPostsMap] = React.useState({});
  const [dailyMoodMap, setDailyMoodMap] = React.useState({});

  const refresh = React.useCallback(() => {
    finalizePastDays();
    setPostsMap(loadAllPosts());
    setDailyMoodMap(loadDailyMoodMap());
  }, []);

  React.useEffect(() => {
    migrateLegacyIfNeeded();
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    const onChanged = () => refresh();
    window.addEventListener("rl-diary-changed", onChanged);
    window.addEventListener("rl-timewarp-changed", onChanged);
    return () => {
      window.removeEventListener("rl-diary-changed", onChanged);
      window.removeEventListener("rl-timewarp-changed", onChanged);
    };
  }, [refresh]);

  if (!available) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10" data-testid="diary-locked-view">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100 text-xl font-semibold"><Lock className="h-5 w-5" /> Diary</div>
          <p className="mt-2 text-slate-600 dark:text-slate-300 text-sm">The Diary becomes available when Week 4 is unlocked. You can unlock Week 4 from the Tracker page (moderator password) or by progressing to Week 4.</p>
          <div className="mt-4"><Button variant="secondary" onClick={() => nav("/tracker")} data-testid="diary-locked-go-tracker-button">Go to Tracker</Button></div>
        </div>
      </div>
    );
  }

  const monthLabel = currentMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const monthIndex = currentMonth.getMonth();
  const year = currentMonth.getFullYear();
  const cells = buildCells(currentMonth);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:py-10" data-testid="diary-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="diary-calendar-header">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="diary-calendar-heading">Diary Calendar</h1>
          <p className="text-sm text-slate-500" data-testid="diary-calendar-subheading">Click any day to open its full diary page.</p>
        </div>
        <Button className="btn-primary" onClick={() => nav(`/diary/new?date=${encodeURIComponent(toDateKey(new Date()))}`)} data-testid="diary-calendar-new-entry-button">
          <Plus className="h-4 w-4 mr-2" /> New Entry
        </Button>
      </div>

      <div
        className="mt-5 rounded-2xl border p-4 sm:p-6"
        style={{
          background: "rgba(2, 8, 23, 0.96)",
          borderColor: "rgba(16, 185, 129, 0.72)",
          boxShadow: "0 0 0 1px rgba(16,185,129,0.28), 0 10px 28px rgba(16,185,129,0.16)",
        }}
        data-testid="diary-calendar-shell"
      >
        <div className="mb-4 flex items-center justify-between" data-testid="diary-calendar-month-nav">
          <button className="rounded-full border border-emerald-400/50 text-emerald-400 px-3 py-2 text-sm hover:bg-emerald-500/10" onClick={() => setCurrentMonth((m) => addMonths(m, -1))} data-testid="diary-calendar-prev-month-button">
            <ChevronLeft className="h-4 w-4 inline-block mr-1" /> Prev
          </button>
          <div className="text-lg md:text-xl font-semibold text-emerald-400" data-testid="diary-calendar-month-label">{monthLabel}</div>
          <button className="rounded-full border border-emerald-400/50 text-emerald-400 px-3 py-2 text-sm hover:bg-emerald-500/10" onClick={() => setCurrentMonth((m) => addMonths(m, 1))} data-testid="diary-calendar-next-month-button">
            Next <ChevronRight className="h-4 w-4 inline-block ml-1" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2 text-xs font-semibold text-emerald-200/90" data-testid="diary-calendar-weekday-row">
          {"Sun Mon Tue Wed Thu Fri Sat".split(" ").map((d) => <div key={d} className="px-1">{d}</div>)}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="diary-calendar-grid">
          {cells.map((d) => {
            const key = toDateKey(d);
            const inMonth = d.getMonth() === monthIndex && d.getFullYear() === year;
            const dayPosts = (postsMap[key] || []).slice().sort((a, b) => (b.ts || 0) - (a.ts || 0));
            const avg = dailyMoodMap[key]?.avg;
            const tint = typeof avg === "number" ? toRgba(moodValueToColor(avg), 0.18) : defaultSiteTint();

            return (
              <button
                key={key}
                className={`relative rounded-xl p-2 text-left min-h-[170px] transition hover:-translate-y-0.5 ${inMonth ? "" : "opacity-70"}`}
                style={{ background: tint }}
                onClick={() => nav(`/diary/day?date=${encodeURIComponent(key)}`)}
                data-testid={`diary-calendar-day-${key}`}
              >
                <div className="absolute left-3 top-2 text-3xl sm:text-4xl font-black leading-none text-emerald-600" data-testid={`diary-calendar-day-number-${key}`}>
                  {d.getDate()}
                </div>

                <div className="mt-12 space-y-1" data-testid={`diary-calendar-day-pills-${key}`}>
                  {dayPosts.slice(0, 3).map((p) => (
                    <div key={p.id} className={`rounded-lg px-2 py-1.5 text-[11px] leading-tight ${postToneClass(p)}`} data-testid={`diary-calendar-post-pill-${key}-${p.id}`}>
                      <div className="font-semibold truncate" data-testid={`diary-calendar-post-title-${key}-${p.id}`}>{postTitle(p)}</div>
                      <div className="opacity-95 truncate">{previewText(p)}</div>
                    </div>
                  ))}
                  {dayPosts.length > 3 && <div className="text-[11px] text-emerald-200">+{dayPosts.length - 3} more entries</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
